import {
  getWorkspaceSettings,
  updateWorkspaceSettings,
  type WorkspaceSettings,
} from "./workspace_settings_helper";

export type TenantUiState = {
  dismissed_tips: string[];
  dns_copied: Record<string, string[]>;
  /** Preferência do setup de envio — wizard | all. */
  sending_setup_wizard_mode: "wizard" | "all";
};

type Cache = {
  loaded: boolean;
  loading: Promise<void> | null;
  state: TenantUiState;
};

const cache: Cache = {
  loaded: false,
  loading: null,
  state: { dismissed_tips: [], dns_copied: {}, sending_setup_wizard_mode: "wizard" },
};

function normalizeUiState(raw: unknown): TenantUiState {
  const obj =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const tips = Array.isArray(obj.dismissed_tips)
    ? obj.dismissed_tips.map(String)
    : [];
  const dnsRaw =
    obj.dns_copied && typeof obj.dns_copied === "object"
      ? (obj.dns_copied as Record<string, unknown>)
      : {};
  const dns_copied: Record<string, string[]> = {};
  Object.keys(dnsRaw).forEach((scope) => {
    const keys = dnsRaw[scope];
    dns_copied[scope] = Array.isArray(keys) ? keys.map(String) : [];
  });
  return {
    dismissed_tips: tips,
    dns_copied,
    sending_setup_wizard_mode:
      obj.sending_setup_wizard_mode === "all" ? "all" : "wizard",
  };
}

/** Carrega ui_state uma vez por sessão de app (LAC-111). */
export async function ensureUiStateLoaded(): Promise<TenantUiState> {
  if (cache.loaded) {
    return cache.state;
  }
  if (!cache.loading) {
    cache.loading = (async () => {
      try {
        const res = await getWorkspaceSettings();
        cache.state = normalizeUiState(res?.data?.ui_state);
      } catch {
        // Offline / 401 — fica local-only até a próxima tentativa.
        cache.state = { dismissed_tips: [], dns_copied: {}, sending_setup_wizard_mode: "wizard" };
      } finally {
        cache.loaded = true;
        cache.loading = null;
      }
    })();
  }
  await cache.loading;
  return cache.state;
}

export function peekUiState(): TenantUiState {
  return cache.state;
}

export function isTipDismissedLocal(tipId: string): boolean {
  try {
    if (localStorage.getItem(`brenvio.pageTip.dismissed.${tipId}`) === "1") {
      return true;
    }
  } catch {
    // ignore
  }
  return cache.state.dismissed_tips.includes(tipId);
}

/** Optimistic local + PATCH merge no tenant. */
export async function dismissTip(tipId: string): Promise<void> {
  try {
    localStorage.setItem(`brenvio.pageTip.dismissed.${tipId}`, "1");
  } catch {
    // ignore
  }
  await ensureUiStateLoaded();
  if (!cache.state.dismissed_tips.includes(tipId)) {
    cache.state = {
      ...cache.state,
      dismissed_tips: [...cache.state.dismissed_tips, tipId],
    };
  }
  try {
    await updateWorkspaceSettings({
      ui_state: { dismissed_tips: cache.state.dismissed_tips },
    } as Partial<WorkspaceSettings>);
  } catch {
    // Mantém local; próximo load tenta de novo.
  }
}

export function loadDnsCopiedKeys(scope: string): string[] {
  if (!scope) {
    return [];
  }
  try {
    const raw = localStorage.getItem(`brenvio.dns.copied.${scope}`);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        return arr.map(String);
      }
    }
  } catch {
    // ignore
  }
  return cache.state.dns_copied[scope] || [];
}

export async function persistDnsCopiedKeys(
  scope: string,
  keys: string[]
): Promise<void> {
  if (!scope) {
    return;
  }
  try {
    localStorage.setItem(
      `brenvio.dns.copied.${scope}`,
      JSON.stringify(keys)
    );
  } catch {
    // ignore
  }
  await ensureUiStateLoaded();
  cache.state = {
    ...cache.state,
    dns_copied: { ...cache.state.dns_copied, [scope]: keys },
  };
  try {
    await updateWorkspaceSettings({
      ui_state: {
        dns_copied: { [scope]: keys },
      },
    } as Partial<WorkspaceSettings>);
  } catch {
    // local ok
  }
}

export async function persistSendingSetupWizardMode(
  mode: "wizard" | "all"
): Promise<void> {
  await ensureUiStateLoaded();
  cache.state = { ...cache.state, sending_setup_wizard_mode: mode };
  try {
    await updateWorkspaceSettings({
      ui_state: { sending_setup_wizard_mode: mode },
    } as Partial<WorkspaceSettings>);
  } catch {
    // local ok
  }
}

/** Só para testes / logout — limpa cache em memória. */
export function resetUiStateCache(): void {
  cache.loaded = false;
  cache.loading = null;
  cache.state = { dismissed_tips: [], dns_copied: {}, sending_setup_wizard_mode: "wizard" };
}
